import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * エリアマップバージョンリストア指示API
 */
export const useRestoreAreaMapVersionAPI = (): {
  state: APIState;
  postRestoreAreaMapVersion: (areaMapVersionId: string) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postRestoreAreaMapVersionAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      areaMapId: number,
      areaMapVersionId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/area_maps/${areaMapId}/versions/${areaMapVersionId}/restore`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postRestoreAreaMapVersion = useAtomCallback(
    useCallback(
      async (get, _, areaMapVersionId: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_restore_area_map_version', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postRestoreAreaMapVersionAPI(
          project.id,
          environment.environment_id,
          environment.area_map_id,
          areaMapVersionId,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_restore_area_map_version', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return false;
        }
        notifySuccess({
          message: t(`api.fms.post_restore_area_map_version`, {
            status: 'successfully',
          }),
        });
        setState('hasValue');
        return true;
      },
      [
        notifyError,
        notifySuccess,
        postRestoreAreaMapVersionAPI,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    postRestoreAreaMapVersion,
  };
};
